import { Box, Flex, Text } from '@chakra-ui/react';
import { Button } from '@morressier/alembic';
import { useFormContext } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import ViewVariables from '../../../components/EmailModule/ViewVariables';
import BrowseTemplates from '../../../components/EmailModule/BrowseTemplates';
import ConfirmTemplateModal from '../../../components/EmailModule/ConfirmTemplateModal';
import Loading from '../../../pages/Status/Loading';
import EditEmailTemplate, {
  IEmailFormType
} from '../../../components/EmailModule/EditEmailTemplate';
import { useQuery } from '@tanstack/react-query';
import { getAllTemplatesByCommand } from '../../../api/email-service';
import { IDomainCommandType } from '@publishing-engine/domain/actions/v1/commands';
import BrowseTemplatesMenu from '../../EmailModule/BrowseTemplatesMenu';

interface Props {
  organizationId: string;
  submissionContextId: string;
  commandName: IDomainCommandType;
  subtype?: string;
  tag?: string;
  revisionSeverity?: string;
  formPayload?: Record<string, unknown>;
}

export type EmailContentForm = {
  emailContent: IEmailFormType;
};

export const EmailContentFormDefaultValues: EmailContentForm = {
  emailContent: {
    body: '',
    subject: '',
    recipients: { to: [], cc: [], bcc: [] },
    attachments: []
  }
};

const ComposeDecisionEmail = ({
  organizationId,
  submissionContextId,
  commandName,
  subtype,
  tag,
  revisionSeverity,
  formPayload
}: Props) => {
  const {
    setValue,
    formState: { isDirty }
  } = useFormContext<EmailContentForm>();

  const [userSelectedIndex, setUserSelectedIndex] = useState<number>(0);
  const [openViewVariables, setOpenViewVariables] = useState(false);
  const [openBrowseTemplates, setOpenBrowseTemplates] = useState(false);
  const [openConfirmTemplateModal, setOpenConfirmTemplateModal] =
    useState(false);
  const [pendingTemplateIndex, setPendingTemplateIndex] = useState<
    number | null
  >(null);

  const [templateName, setTemplateName] = useState<string>('Decision Template');

  const { data, isLoading: isLoadingTemplates } = useQuery({
    queryKey: [
      'browse-email-templates',
      organizationId,
      commandName,
      subtype,
      tag,
      submissionContextId
    ],
    queryFn: () =>
      getAllTemplatesByCommand({
        organizationId,
        commandName,
        query: { subtype, tag, submissionContextId }
      })
  });

  const emailTemplates = data?.data;
  const selectedTemplate = useMemo(() => {
    return (
      emailTemplates?.[userSelectedIndex] ?? {
        body: '',
        subject: '',
        recipients: { to: [], cc: [], bcc: [] } as {
          to: string[];
          cc: string[];
          bcc: string[];
        },
        attachments: []
      }
    );
  }, [emailTemplates, userSelectedIndex]);

  const eventName = emailTemplates?.[userSelectedIndex]?.event;

  useEffect(() => {
    if (selectedTemplate) {
      setValue(
        'emailContent.recipients.to',
        selectedTemplate.recipients.to
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );

      setValue(
        'emailContent.recipients.cc',
        selectedTemplate.recipients.cc
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );
      setValue(
        'emailContent.recipients.bcc',
        selectedTemplate.recipients.bcc
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );
      setValue('emailContent.subject', selectedTemplate.subject);
      setValue('emailContent.body', selectedTemplate.body);
    }
  }, [selectedTemplate, setValue]);

  useEffect(() => {
    if (!emailTemplates || emailTemplates.length === 0) return;
    const defaultIndex = emailTemplates.findIndex(t => t.default);
    const selectedIndex = defaultIndex === -1 ? 0 : defaultIndex;
    setUserSelectedIndex(selectedIndex);
    applyTemplate(selectedIndex);
    setTemplateName(emailTemplates[selectedIndex]?.name ?? 'Decision Template');
  }, [emailTemplates]);

  const applyTemplate = (index: number) => {
    const template = emailTemplates?.[index];
    if (!template) return;
    setTemplateName(template.name);
    setValue(
      'emailContent.recipients.to',
      template.recipients.to
        ?.filter(email => email.trim() !== '')
        .map(email => ({ text: email })) ?? []
    );

    setValue(
      'emailContent.recipients.cc',
      template.recipients.cc
        ?.filter(email => email.trim() !== '')
        .map(email => ({ text: email })) ?? []
    );
    setValue(
      'emailContent.recipients.bcc',
      template.recipients.bcc
        ?.filter(email => email.trim() !== '')
        .map(email => ({ text: email })) ?? []
    );
    setValue('emailContent.subject', template.subject);
    setValue('emailContent.body', template.body);
  };

  const handleSelectTemplate = (index: number) => {
    if (index === userSelectedIndex) {
      setOpenBrowseTemplates(false);
      return;
    }
    if (userSelectedIndex === null || !isDirty) {
      applyTemplate(index);
      setUserSelectedIndex(index);
      setOpenBrowseTemplates(false);
    } else {
      setPendingTemplateIndex(index);
      setOpenBrowseTemplates(false);
      setOpenConfirmTemplateModal(true);
    }
  };

  if (isLoadingTemplates) {
    return <Loading />;
  }

  return (
    <Box backgroundColor="surfacePrimaryFill" mt={3}>
      <Flex flexDirection="column" gap={2}>
        <Flex justifyContent="space-between" alignItems="center">
          <Text textStyle="headingGroup">{templateName}</Text>
          <Flex gap={4}>
            {emailTemplates && emailTemplates.length > 1 && (
              <BrowseTemplatesMenu
                templates={emailTemplates}
                selectedIndex={userSelectedIndex}
                onSelectTemplate={index => {
                  setUserSelectedIndex(index);
                  applyTemplate(index);
                }}
                onOpenDetailView={() => setOpenBrowseTemplates(true)}
              />
            )}
            <Button
              variant="secondary"
              onClick={() => setOpenViewVariables(true)}
            >
              View Variables
            </Button>
          </Flex>
        </Flex>

        {selectedTemplate && (
          <EditEmailTemplate
            isCustomTemplate
            showTestEmail
            eventName={eventName}
            revisionSeverity={revisionSeverity}
            formPayload={formPayload}
          />
        )}
      </Flex>

      {openViewVariables && (
        <ViewVariables onClose={() => setOpenViewVariables(false)} />
      )}
      {openBrowseTemplates && emailTemplates?.length && (
        <BrowseTemplates
          templates={emailTemplates}
          onSelectTemplate={handleSelectTemplate}
          onClose={() => setOpenBrowseTemplates(false)}
          defaultIndex={userSelectedIndex}
          isCustomTemplate={false}
        />
      )}
      {openConfirmTemplateModal && (
        <ConfirmTemplateModal
          onClose={() => {
            setOpenConfirmTemplateModal(false);
            setOpenBrowseTemplates(true);
            setPendingTemplateIndex(null);
          }}
          onConfirm={() => {
            if (pendingTemplateIndex === null) return;
            applyTemplate(pendingTemplateIndex);
            setUserSelectedIndex(pendingTemplateIndex);
            setOpenConfirmTemplateModal(false);
            setPendingTemplateIndex(null);
          }}
        />
      )}
    </Box>
  );
};

export default ComposeDecisionEmail;
